import { useSyncExternalStore } from 'react';

export type SidebarMode = 'dark' | 'light';

const STORAGE_KEY = 'lognai-sidebar-mode';
const VALID_MODES: readonly SidebarMode[] = ['dark', 'light'];

function getStoredMode(): SidebarMode {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY) as SidebarMode | null;
    return stored && VALID_MODES.includes(stored) ? stored : 'dark';
  } catch {
    return 'dark';
  }
}

// Module-level singleton — shared across all hook instances in the same tab.
let currentMode: SidebarMode = getStoredMode();
const listeners = new Set<() => void>();

function applyMode(mode: SidebarMode) {
  document.documentElement.setAttribute('data-sidebar-mode', mode);
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {}
}

// Apply on module load so the attribute is set before first render.
applyMode(currentMode);

function subscribe(callback: () => void) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

function getSnapshot() {
  return currentMode;
}

export function setMode(next: SidebarMode) {
  currentMode = next;
  applyMode(next);
  listeners.forEach((l) => l());
}

export function useSidebarMode() {
  const mode = useSyncExternalStore(subscribe, getSnapshot);
  return { mode, setMode };
}
